import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Tag, DatePicker, Empty,
  Popconfirm, Progress, InputNumber, Timeline, Avatar, Tooltip, Alert,
} from 'antd';
import {
  Plus, Edit2, Trash2, Users, CheckCircle2, AlertTriangle, ClipboardList,
  MessageSquarePlus, Pencil, GitBranch,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import useApiMethods from '../../lib/useApiMethods';
import {
  ASSIGNMENTS_TASKS, ASSIGNMENTS_SUMMARY, PEOPLE,
} from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';
import { MetricCard } from '../../components/insights';

dayjs.extend(relativeTime);

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_COLORS = { low: 'default', medium: 'blue', high: 'orange', urgent: 'red' };

const progressColor = (pct) => pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name[0].toUpperCase();
};

const AVATAR_COLORS = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777'];
const avatarColor = (name) => AVATAR_COLORS[Math.abs([...name].reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_COLORS.length];

const Assignments = () => {
  const { fetch, create, update, remove } = useApiMethods();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({});
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);

  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [parentForSubtask, setParentForSubtask] = useState(null);
  const [taskForm] = Form.useForm();
  const dueDateWatch = Form.useWatch('due_date', taskForm);

  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressTask, setProgressTask] = useState(null);
  const [editingProgress, setEditingProgress] = useState(null);
  const [progressForm] = Form.useForm();

  const [taskFilters, setTaskFilters] = useState({});

  const loadData = useCallback(async () => {
    const [taskRes, sumRes, peopleRes] = await Promise.all([
      fetch(ASSIGNMENTS_TASKS, { ...taskFilters, page_size: 1000 }, setLoading),
      fetch(ASSIGNMENTS_SUMMARY),
      fetch(PEOPLE),
    ]);
    if (taskRes?.status === 'success') setTasks(taskRes.data?.results || []);
    if (sumRes?.status === 'success') setSummary(sumRes.data || {});
    if (peopleRes?.status === 'success') {
      const p = peopleRes.data || [];
      setPeople(Array.isArray(p) ? p : []);
    }
  }, [taskFilters]);

  useEffect(() => { loadData(); }, [loadData]);

  const peopleOptions = people.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  // ── Task CRUD ──
  const openNewTask = () => {
    setEditingTask(null); setParentForSubtask(null); taskForm.resetFields(); setTaskModalOpen(true);
  };
  const openNewSubtask = (parentTask) => {
    const proceed = () => {
      setEditingTask(null);
      setParentForSubtask(parentTask);
      taskForm.resetFields();
      setTaskModalOpen(true);
    };
    const isFirst = !parentTask.has_subtasks;
    const hasExistingProgress = (parentTask.manual_progress || 0) > 0;
    if (isFirst && hasExistingProgress) {
      Modal.confirm({
        title: 'Switch progress to auto-calculated?',
        content: `Adding subtasks switches "${parentTask.title}" to auto-calculated progress from subtasks. The current ${parentTask.manual_progress}% will be replaced. Continue?`,
        okText: 'Add Subtask',
        cancelText: 'Cancel',
        okButtonProps: { style: { backgroundColor: '#4f46e5' } },
        onOk: proceed,
      });
    } else {
      proceed();
    }
  };
  const openEditTask = (task) => {
    setEditingTask(task); setParentForSubtask(null);
    taskForm.setFieldsValue({
      title: task.title, description: task.description,
      responsible_id: task.responsible?.id,
      member_ids: (task.members || []).map((m) => m.id),
      priority: task.priority,
      status: task.status,
      start_date: task.start_date ? dayjs(task.start_date) : null,
      due_date: task.due_date ? dayjs(task.due_date) : null,
    });
    setTaskModalOpen(true);
  };
  const closeTaskModal = () => {
    setTaskModalOpen(false); setEditingTask(null); setParentForSubtask(null); taskForm.resetFields();
  };
  const handleTaskSave = async (values) => {
    const payload = {
      title: values.title, description: values.description || '',
      responsible_id: values.responsible_id,
      member_ids: values.member_ids || [],
      priority: values.priority || 'medium', status: values.status || 'todo',
      start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
      due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
    };
    if (parentForSubtask && !editingTask) payload.parent_task_id = parentForSubtask.id;
    let res;
    if (editingTask) res = await update(`${ASSIGNMENTS_TASKS}${editingTask.id}/`, payload);
    else res = await create(ASSIGNMENTS_TASKS, payload);
    if (res?.status !== 'fail') {
      ToastMsg('success', editingTask ? 'Updated' : (parentForSubtask ? 'Subtask created' : 'Created'));
      closeTaskModal(); loadData();
    }
  };
  const handleTaskDelete = async (id) => {
    const res = await remove(`${ASSIGNMENTS_TASKS}${id}/`);
    if (res?.status !== 'fail') { ToastMsg('success', 'Deleted'); loadData(); }
  };
  const handleStatusChange = async (taskId, newStatus) => {
    const res = await update(`${ASSIGNMENTS_TASKS}${taskId}/`, { status: newStatus });
    if (res?.status !== 'fail') loadData();
  };

  // ── Progress Update ──
  const openProgressModal = (task) => {
    setProgressTask(task);
    setEditingProgress(null);
    progressForm.resetFields();
    progressForm.setFieldsValue({ progress: task.manual_progress || 0 });
    setProgressModalOpen(true);
  };
  const openEditProgress = (task, update) => {
    setProgressTask(task);
    setEditingProgress(update);
    progressForm.resetFields();
    progressForm.setFieldsValue({ progress: update.progress, comment: update.comment });
    setProgressModalOpen(true);
  };
  const closeProgressModal = () => {
    setProgressModalOpen(false);
    setProgressTask(null);
    setEditingProgress(null);
    progressForm.resetFields();
  };
  const handleProgressSave = async (values) => {
    const payload = { progress: values.progress, comment: values.comment || '' };
    let res;
    if (editingProgress) {
      res = await update(`${ASSIGNMENTS_TASKS}${progressTask.id}/progress/${editingProgress.id}/`, payload);
    } else {
      res = await create(`${ASSIGNMENTS_TASKS}${progressTask.id}/progress/`, payload);
    }
    if (res?.status !== 'fail') {
      ToastMsg('success', editingProgress ? 'Progress update edited' : 'Progress updated');
      closeProgressModal(); loadData();
    }
  };
  const handleProgressDelete = async (task, updateId) => {
    const res = await remove(`${ASSIGNMENTS_TASKS}${task.id}/progress/${updateId}/`);
    if (res?.status !== 'fail') { ToastMsg('success', 'Progress update deleted'); loadData(); }
  };

  const taskColumns = [
    {
      title: 'Title', dataIndex: 'title', key: 'title',
      render: (text, r) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{text}</span>
            {r.has_subtasks && (
              <Tag color="purple" className="!m-0">
                <GitBranch size={10} className="inline mr-1" />
                {r.subtasks_done}/{r.subtask_count}
              </Tag>
            )}
          </div>
          {r.description && (
            <Tooltip title={r.description} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
              <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2 cursor-help">{r.description}</div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Responsible', key: 'responsible', width: 140,
      render: (_, r) => {
        const person = r.responsible;
        if (!person) return '-';
        return (
          <div className="flex items-center gap-2">
            <Avatar size={24} style={{ backgroundColor: avatarColor(person.name), fontSize: 11 }}>
              {initials(person.name)}
            </Avatar>
            <span className="text-sm">{person.name}</span>
          </div>
        );
      },
    },
    {
      title: 'Members', key: 'members', width: 140,
      render: (_, r) => {
        const members = r.members || [];
        if (!members.length) return <span className="text-muted-foreground text-xs">—</span>;
        return (
          <Avatar.Group size={24} max={{ count: 3, style: { backgroundColor: '#4f46e5', fontSize: 10 } }}>
            {members.map((m) => (
              <Tooltip key={m.id} title={m.name}>
                <Avatar size={24} style={{ backgroundColor: avatarColor(m.name), fontSize: 11 }}>
                  {initials(m.name)}
                </Avatar>
              </Tooltip>
            ))}
          </Avatar.Group>
        );
      },
    },
    {
      title: 'Progress', key: 'progress', width: 200,
      render: (_, r) => {
        const pct = Math.round(r.overall_progress || 0);
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Progress percent={pct} size="small" strokeColor={progressColor(pct)} />
              {r.has_subtasks && (
                <div className="text-[10px] text-muted-foreground leading-tight">auto · from subtasks</div>
              )}
            </div>
            {r.can_update_progress && !r.has_subtasks && (
              <Tooltip title="Update progress">
                <Button type="link" size="small" className="p-0" onClick={(e) => { e.stopPropagation(); openProgressModal(r); }}>
                  <MessageSquarePlus size={14} />
                </Button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Priority', dataIndex: 'priority', key: 'priority', width: 90,
      render: (val) => <Tag color={PRIORITY_COLORS[val]}>{val}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 140,
      render: (val, r) => (r.can_edit || r.can_update_status) ? (
        <Select size="small" value={val} onChange={(v) => handleStatusChange(r.id, v)}
          options={STATUS_OPTIONS} style={{ width: 130 }} variant="borderless" />
      ) : (
        <Tag>{STATUS_OPTIONS.find((o) => o.value === val)?.label || val}</Tag>
      ),
    },
    {
      title: 'Start', dataIndex: 'start_date', key: 'start_date', width: 90,
      render: (val) => val ? dayjs(val).format('DD MMM') : '-',
    },
    {
      title: 'Due', dataIndex: 'due_date', key: 'due_date', width: 90,
      render: (val) => {
        if (!val) return '-';
        const overdue = dayjs(val).isBefore(dayjs(), 'day');
        return <span className={overdue ? 'text-red-500 font-medium' : ''}>{dayjs(val).format('DD MMM')}</span>;
      },
    },
    {
      title: '', key: 'actions', width: 120,
      render: (_, r) => (
        <div className="flex gap-1">
          {r.can_add_subtask && (
            <Tooltip title="Add subtask">
              <Button type="text" size="small" icon={<GitBranch size={14} />} onClick={() => openNewSubtask(r)} />
            </Tooltip>
          )}
          {r.can_edit && (
            <>
              <Tooltip title="Edit">
                <Button type="text" size="small" icon={<Edit2 size={14} />} onClick={() => openEditTask(r)} />
              </Tooltip>
              <Popconfirm
                title={r.has_subtasks ? `Delete task and ${r.subtask_count} subtask(s)?` : 'Delete?'}
                onConfirm={() => handleTaskDelete(r.id)} okType="danger"
              >
                <Tooltip title="Delete">
                  <Button type="text" size="small" danger icon={<Trash2 size={14} />} />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </div>
      ),
    },
  ];

  // ── Reusable progress timeline (used for both parent task and subtask expanded rows) ──
  const renderProgressTimeline = (record) => {
    const updates = record.progress_updates || [];
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Progress Updates</h4>
          {record.can_update_progress && (
            <Button size="small" icon={<MessageSquarePlus size={12} />} onClick={() => openProgressModal(record)}>
              Add Update
            </Button>
          )}
        </div>
        {updates.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-4 bg-background rounded-lg border border-dashed border-border">
            No updates yet.{record.can_update_progress ? ' Click "Add Update" to log progress.' : ''}
          </div>
        ) : (
          <Timeline
            items={updates.map((u) => ({
              color: u.progress >= 80 ? 'green' : u.progress >= 50 ? 'orange' : 'red',
              children: (
                <div className="pb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{u.progress}%</span>
                    <span className="text-xs text-muted-foreground">by {u.updated_by?.name || 'Unknown'}</span>
                    <span className="text-xs text-muted-foreground">· {dayjs(u.created_date).fromNow()}</span>
                    {record.can_update_progress && (
                      <div className="flex gap-0.5 ml-auto">
                        <Tooltip title="Edit">
                          <Button type="text" size="small" icon={<Pencil size={12} />}
                            onClick={() => openEditProgress(record, u)} />
                        </Tooltip>
                        <Popconfirm title="Delete this progress update?" okType="danger"
                          onConfirm={() => handleProgressDelete(record, u.id)}>
                          <Tooltip title="Delete">
                            <Button type="text" size="small" danger icon={<Trash2 size={12} />} />
                          </Tooltip>
                        </Popconfirm>
                      </div>
                    )}
                  </div>
                  {u.comment && <div className="text-sm text-foreground mt-1">{u.comment}</div>}
                </div>
              ),
            }))}
          />
        )}
      </div>
    );
  };

  // ── Subtask table columns (compact, for the inline table inside an expanded parent) ──
  const subtaskColumns = [
    {
      title: 'Subtask', dataIndex: 'title', key: 'title',
      render: (text, r) => (
        <div>
          <span className="text-sm">{text}</span>
          {r.due_date_exceeds_parent && (
            <Tooltip title="Due date is after the parent task's due date">
              <AlertTriangle size={12} className="inline ml-1.5 text-amber-500" />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Responsible', key: 'responsible', width: 140,
      render: (_, r) => r.responsible ? (
        <div className="flex items-center gap-2">
          <Avatar size={20} style={{ backgroundColor: avatarColor(r.responsible.name), fontSize: 10 }}>
            {initials(r.responsible.name)}
          </Avatar>
          <span className="text-xs">{r.responsible.name}</span>
        </div>
      ) : '-',
    },
    {
      title: 'Progress', key: 'progress', width: 160,
      render: (_, r) => {
        const pct = Math.round(r.overall_progress || 0);
        return (
          <div className="flex items-center gap-2">
            <Progress percent={pct} size="small" strokeColor={progressColor(pct)} className="!mb-0 flex-1" />
            {r.can_update_progress && (
              <Button type="link" size="small" className="p-0" onClick={() => openProgressModal(r)}>
                <MessageSquarePlus size={12} />
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (val, r) => (r.can_edit || r.can_update_status) ? (
        <Select size="small" value={val} onChange={(v) => handleStatusChange(r.id, v)}
          options={STATUS_OPTIONS} style={{ width: 120 }} variant="borderless" />
      ) : (
        <Tag>{STATUS_OPTIONS.find((o) => o.value === val)?.label || val}</Tag>
      ),
    },
    {
      title: 'Due', dataIndex: 'due_date', key: 'due_date', width: 80,
      render: (val) => {
        if (!val) return '-';
        const overdue = dayjs(val).isBefore(dayjs(), 'day');
        return <span className={`text-xs ${overdue ? 'text-red-500 font-medium' : ''}`}>{dayjs(val).format('DD MMM')}</span>;
      },
    },
    {
      title: '', key: 'actions', width: 70,
      render: (_, r) => r.can_edit ? (
        <div className="flex gap-1">
          <Button type="text" size="small" icon={<Edit2 size={12} />} onClick={() => openEditTask(r)} />
          <Popconfirm title="Delete subtask?" onConfirm={() => handleTaskDelete(r.id)} okType="danger">
            <Button type="text" size="small" danger icon={<Trash2 size={12} />} />
          </Popconfirm>
        </div>
      ) : null,
    },
  ];

  // ── Expanded row: subtasks + progress timeline ──
  const expandedRowRender = (record) => {
    const updates = record.progress_updates || [];
    const members = record.members || [];
    const subtasks = record.subtasks || [];

    return (
      <div className="px-4 py-3 space-y-4">
        {/* Subtasks */}
        {(subtasks.length > 0 || record.can_add_subtask) && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Subtasks {subtasks.length > 0 && <span className="ml-1 text-foreground/70">({record.subtasks_done}/{record.subtask_count} done)</span>}
              </h4>
              {record.can_add_subtask && (
                <Button size="small" icon={<Plus size={12} />} onClick={() => openNewSubtask(record)}>
                  Add Subtask
                </Button>
              )}
            </div>
            {subtasks.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-3 bg-background rounded-lg border border-dashed border-border">
                No subtasks yet.
              </div>
            ) : (
              <div className="rounded-lg border border-border overflow-hidden">
                <Table dataSource={subtasks} columns={subtaskColumns} rowKey="id"
                  pagination={false} size="small" showHeader
                  expandable={{
                    expandedRowRender: (sub) => (
                      <div className="px-3 py-2 bg-background">{renderProgressTimeline(sub)}</div>
                    ),
                    rowExpandable: () => true,
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Members list */}
        {members.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Team Members</h4>
            <div className="flex flex-wrap gap-2">
              {members.map((m) => (
                <div key={m.id} className="flex items-center gap-2 bg-background rounded-lg px-3 py-1.5 border border-border">
                  <Avatar size={20} style={{ backgroundColor: avatarColor(m.name), fontSize: 10 }}>
                    {initials(m.name)}
                  </Avatar>
                  <span className="text-sm">{m.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress timeline (parent's own — kept for history even if subtasks now own progress) */}
        {(updates.length > 0 || record.can_update_progress) && renderProgressTimeline(record)}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Assignments</h2>
        <p className="text-sm text-muted-foreground">Track tasks and progress</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard icon={ClipboardList} intent="info" title="Total Tasks"
          value={summary.tasks_total || 0} subtitle={`${summary.tasks_in_progress || 0} in progress`} />
        <MetricCard icon={Users} intent="primary" title="To Do" value={summary.tasks_todo || 0} />
        <MetricCard icon={CheckCircle2} intent="positive" title="Completed" value={summary.tasks_done || 0} />
        <MetricCard icon={AlertTriangle} intent={summary.tasks_overdue > 0 ? 'negative' : 'neutral'}
          title="Overdue" value={summary.tasks_overdue || 0} goodUp={false} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={taskFilters.status ?? ''}
          options={[{ value: '', label: 'All Status' }, ...STATUS_OPTIONS]}
          onChange={(v) => setTaskFilters((f) => {
            const next = { ...f };
            if (!v) delete next.status; else next.status = v;
            return next;
          })} style={{ width: 160 }} />
        <Select value={taskFilters.responsible ?? ''} showSearch optionFilterProp="label"
          options={[{ value: '', label: 'All Responsible' }, ...peopleOptions]}
          onChange={(v) => setTaskFilters((f) => {
            const next = { ...f };
            if (!v) delete next.responsible; else next.responsible = v;
            return next;
          })} style={{ width: 200 }} />
        <Select value={taskFilters.member ?? ''} showSearch optionFilterProp="label"
          options={[{ value: '', label: 'All Members' }, ...peopleOptions]}
          onChange={(v) => setTaskFilters((f) => {
            const next = { ...f };
            if (!v) delete next.member; else next.member = v;
            return next;
          })} style={{ width: 200 }} />
        <Select value={taskFilters.priority ?? ''}
          options={[{ value: '', label: 'All Priority' }, ...PRIORITY_OPTIONS]}
          onChange={(v) => setTaskFilters((f) => {
            const next = { ...f };
            if (!v) delete next.priority; else next.priority = v;
            return next;
          })} style={{ width: 150 }} />
        <Button type="primary" icon={<Plus size={14} />} onClick={openNewTask}
          style={{ backgroundColor: '#4f46e5', marginLeft: 'auto' }}>New Task</Button>
      </div>

      <div className="rounded-2xl bg-card border border-border shadow-sm">
        <Table dataSource={tasks} columns={taskColumns} rowKey="id" loading={loading}
          pagination={{ defaultPageSize: 25, pageSizeOptions: [25, 50, 100, 200], showSizeChanger: true, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}` }} size="middle"
          locale={{ emptyText: <Empty description="No tasks yet" /> }}
          expandable={{ expandedRowRender, rowExpandable: () => true }} />
      </div>

      {/* Task Create/Edit Modal */}
      <Modal
        title={editingTask ? 'Edit Task' : (parentForSubtask ? 'New Subtask' : 'New Task')}
        open={taskModalOpen} width={540}
        onCancel={closeTaskModal}
        onOk={() => taskForm.submit()}
        okText={editingTask ? 'Update' : (parentForSubtask ? 'Create Subtask' : 'Create')}
        okButtonProps={{ style: { backgroundColor: '#4f46e5' } }}
      >
        {parentForSubtask && (
          <Alert
            type="info" showIcon
            message={<span>Subtask of: <strong>{parentForSubtask.title}</strong>{parentForSubtask.due_date ? ` · parent due ${dayjs(parentForSubtask.due_date).format('DD MMM')}` : ''}</span>}
            className="!mb-3"
          />
        )}
        <Form form={taskForm} layout="vertical" onFinish={handleTaskSave}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder={parentForSubtask ? 'e.g., Draft the API spec' : 'e.g., Sprint Delivery Q2'} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional details" />
          </Form.Item>
          <Form.Item name="responsible_id" label="Responsible" rules={[{ required: true, message: 'Select a responsible person' }]}>
            <Select showSearch optionFilterProp="label" options={peopleOptions} placeholder="Who is accountable for this task?" />
          </Form.Item>
          <Form.Item name="member_ids" label="Members">
            <Select mode="multiple" showSearch optionFilterProp="label" options={peopleOptions}
              placeholder="Who will work on this task?" />
          </Form.Item>
          <div className="grid grid-cols-3 gap-3">
            <Form.Item name="priority" label="Priority" initialValue="medium">
              <Select options={PRIORITY_OPTIONS} />
            </Form.Item>
            <Form.Item name="start_date" label="Start Date">
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item name="due_date" label="Due Date">
              <DatePicker className="w-full" />
            </Form.Item>
            {parentForSubtask && parentForSubtask.due_date && dueDateWatch
              && dayjs(dueDateWatch).isAfter(dayjs(parentForSubtask.due_date), 'day') && (
              <div className="col-span-3 -mt-2 text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle size={12} />
                This is after the parent task's due date ({dayjs(parentForSubtask.due_date).format('DD MMM')}).
              </div>
            )}
            {editingTask && (
              <Form.Item name="status" label="Status">
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            )}
          </div>
        </Form>
      </Modal>

      {/* Progress Update Modal */}
      <Modal title={editingProgress ? 'Edit Progress Update' : 'Update Progress'} open={progressModalOpen}
        onCancel={closeProgressModal}
        onOk={() => progressForm.submit()} okText={editingProgress ? 'Save Changes' : 'Save Update'}
        okButtonProps={{ style: { backgroundColor: '#4f46e5' } }}>
        <Form form={progressForm} layout="vertical" onFinish={handleProgressSave}>
          <Form.Item name="progress" label="Completion %" rules={[{ required: true }]}>
            <InputNumber min={0} max={100} className="w-full" placeholder="e.g., 60" addonAfter="%" />
          </Form.Item>
          <Form.Item name="comment" label="Comment" rules={[{ required: true, message: 'Add a comment' }]}>
            <Input.TextArea rows={3} placeholder="What was done? What's the current status?" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Assignments;
